import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth, onAuthStateChanged, RecaptchaVerifier, PhoneAuthProvider, linkWithCredential, signOut } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase-config';
import { SecureLog } from '../utils/SecurityLogger';
import THEME from '../theme';
import { motion, AnimatePresence } from 'framer-motion';

// ======================== SECURITY HELPERS ========================

// [SECURITY] Name validation — block special chars, scripts, emoji
const validateName = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return { valid: false, error: 'Name is required.' };
    if (trimmed.length < 2) return { valid: false, error: 'Name must be at least 2 characters.' };
    if (trimmed.length > 50) return { valid: false, error: 'Name must be less than 50 characters.' };
    // Allow letters, spaces, periods, hyphens, apostrophes (multilingual support)
    const safeNameRegex = /^[a-zA-Z\s.'\-\u0900-\u097F\u0C80-\u0CFF\u0D00-\u0D7F]+$/;
    if (!safeNameRegex.test(trimmed)) return { valid: false, error: 'Name contains invalid characters.' };
    if (/<script|<\/script|javascript:|on\w+\s*=/i.test(trimmed)) return { valid: false, error: 'Invalid input detected.' };
    return { valid: true, error: '' };
};

// [SECURITY] Phone number validation — Indian mobile prefixes
const validatePhone = (phone) => {
    if (phone.length !== 10) return { valid: false, error: 'Enter a valid 10-digit number.' };
    if (!/^[6-9]\d{9}$/.test(phone)) return { valid: false, error: 'Phone must start with 6, 7, 8, or 9.' };
    return { valid: true, error: '' };
};

// ======================== ANIMATION VARIANTS ========================

const stepTransition = { type: 'spring', stiffness: 280, damping: 24 };

const contentVariants = {
    enter: { opacity: 0, x: 30 },
    center: { opacity: 1, x: 0, transition: stepTransition },
    exit: { opacity: 0, x: -30, transition: { duration: 0.15 } }
};

const otpDigitVariants = {
    empty: { scale: 1, borderColor: THEME.colors.borderColor },
    filled: {
        scale: [1, 1.12, 1],
        borderColor: THEME.colors.goldAccent,
        transition: { scale: { duration: 0.2 }, borderColor: { duration: 0.15 } }
    }
};

const confettiVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: (i) => ({
        opacity: [1, 1, 0],
        scale: [0, 1.2, 0.5],
        x: [0, (i % 2 === 0 ? 1 : -1) * (20 + Math.random() * 40)],
        y: [0, -(30 + Math.random() * 50), 20],
        rotate: [0, (i % 2 === 0 ? 1 : -1) * (90 + Math.random() * 180)],
        transition: { duration: 0.8 + Math.random() * 0.4, delay: i * 0.04 }
    })
};

const confettiColors = ['#F59E0B', '#14B8A6', '#818CF8', '#F472B6', '#34D399', '#FBBF24'];

// ======================== COMPONENTS ========================

// --- STEP INDICATOR with animated progress bar ---
const StepIndicator = ({ currentStep }) => {
    const steps = [
        { label: 'Institute', icon: '🏫' },
        { label: 'Profile', icon: '👤' },
        { label: 'Verify', icon: '✓' }
    ];
    return (
        <div className="flex items-center justify-center gap-1 mb-8">
            {steps.map((step, i) => {
                const isActive = i + 1 === currentStep;
                const isDone = i + 1 < currentStep;
                return (
                    <React.Fragment key={i}>
                        <div className="flex flex-col items-center gap-1.5">
                            <motion.div
                                animate={{
                                    backgroundColor: isDone ? '#14B8A6' : isActive ? '#F59E0B' : '#1E293B',
                                    scale: isActive ? 1.05 : 1,
                                    boxShadow: isActive ? '0 4px 12px rgba(245,158,11,0.25)' : isDone ? '0 4px 12px rgba(20,184,166,0.2)' : 'none',
                                }}
                                transition={stepTransition}
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border border-slate-700"
                                style={{ color: isDone || isActive ? (isDone ? 'white' : THEME.colors.midnightBlue) : '#64748B' }}
                            >
                                {isDone ? '✓' : step.icon}
                            </motion.div>
                            <span className={`text-[10px] font-medium tracking-wide uppercase ${isActive ? 'text-amber-400' : isDone ? 'text-teal-400' : 'text-slate-600'}`}>
                                {step.label}
                            </span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className="relative w-12 h-0.5 mb-5 bg-slate-700 rounded-full overflow-hidden">
                                <motion.div
                                    className="absolute inset-y-0 left-0 rounded-full"
                                    animate={{ width: isDone ? '100%' : '0%', backgroundColor: '#14B8A6' }}
                                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

// --- OTP Input with micro-animations ---
const OtpInput = ({ value, onChange, disabled }) => {
    const inputRefs = useRef([]);
    const digits = value.padEnd(6, '').split('').slice(0, 6);

    const handleChange = (index, val) => {
        if (!/^\d*$/.test(val)) return;
        const newDigits = [...digits];
        newDigits[index] = val;
        const newOtp = newDigits.join('').slice(0, 6);
        onChange(newOtp);
        if (val && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted) {
            onChange(pasted);
            const focusIdx = Math.min(pasted.length, 5);
            inputRefs.current[focusIdx]?.focus();
        }
    };

    // [ANIMATION] Shimmer when all 6 digits are filled
    const allFilled = digits.every(d => d);

    return (
        <div className="relative">
            <div className="flex gap-2.5 justify-center">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <motion.input
                        key={i}
                        ref={el => inputRefs.current[i] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        disabled={disabled}
                        value={digits[i] || ''}
                        onChange={(e) => handleChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        onPaste={i === 0 ? handlePaste : undefined}
                        className="w-12 h-14 text-center text-lg font-mono font-bold rounded-xl border-2 transition-colors duration-200 focus:outline-none disabled:opacity-50"
                        style={{
                            backgroundColor: THEME.colors.inputBg,
                            color: 'white',
                            boxShadow: digits[i] ? '0 0 0 3px rgba(245, 158, 11, 0.12)' : 'none',
                        }}
                        variants={otpDigitVariants}
                        animate={digits[i] ? 'filled' : 'empty'}
                    />
                ))}
            </div>
            {/* [ANIMATION] Shimmer overlay when all digits entered */}
            {allFilled && !disabled && (
                <motion.div
                    initial={{ opacity: 0, x: '-100%' }}
                    animate={{ opacity: [0, 0.3, 0], x: '200%' }}
                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.3), transparent)', borderRadius: '0.75rem' }}
                />
            )}
        </div>
    );
};

// --- Custom Select Dropdown ---
const SelectField = ({ label, value, onChange, options, placeholder, icon, disabled, tooltip }) => (
    <div className="relative group">
        <label className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 block">{label}</label>
        <div className="relative">
            {icon && (
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-500">
                    {icon}
                </div>
            )}
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-full py-3.5 rounded-xl text-sm appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none"
                style={{
                    backgroundColor: THEME.colors.inputBg,
                    border: `1px solid ${value ? THEME.colors.goldAccent + '40' : THEME.colors.borderColor}`,
                    color: value ? 'white' : THEME.colors.textSecondary,
                    paddingLeft: icon ? '2.75rem' : '1rem',
                    paddingRight: '2.5rem',
                    boxShadow: value ? '0 0 0 2px rgba(245, 158, 11, 0.08)' : 'none',
                }}
            >
                <option value="" disabled>{placeholder}</option>
                {options.map((opt) => (
                    <option key={opt} value={opt} style={{ backgroundColor: THEME.colors.midnightSurface, color: 'white' }}>
                        {opt}
                    </option>
                ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </div>
        </div>
        {/* [FEATURE] Tooltip on hover */}
        {tooltip && (
            <div className="absolute left-0 right-0 bottom-full mb-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-20">
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-xs text-slate-300 shadow-xl">
                    {tooltip}
                </div>
            </div>
        )}
    </div>
);

// ======================== MAIN COMPONENT ========================

function ProfileSetup() {
    const navigate = useNavigate();
    const location = useLocation();
    const authInstance = getAuth();
    const [user, setUser] = useState(authInstance.currentUser);

    const institute = location.state?.institute;

    // [SECURITY] Auth guard
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(authInstance, (u) => {
            if (!u) {
                SecureLog.warn('[ProfileSetup] Unauthenticated access — redirecting.');
                navigate('/login');
            } else {
                setUser(u);
            }
        });
        return () => unsubscribe();
    }, [authInstance, navigate]);

    useEffect(() => {
        if (!institute) {
            navigate('/institute-selection');
        }
    }, [institute, navigate]);

    // Form states
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [course, setCourse] = useState('');
    const [batch, setBatch] = useState('');
    const [diet, setDiet] = useState('');
    const [hostel, setHostel] = useState('');
    const [contact, setContact] = useState('');
    const [phoneError, setPhoneError] = useState('');

    // OTP states
    const [otpCode, setOtpCode] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [verificationId, setVerificationId] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);

    // Save states
    const [isSaving, setIsSaving] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [error, setError] = useState('');

    const recaptchaVerifierRef = useRef(null);
    const recaptchaContainerRef = useRef(null);

    // Dynamic options from institute — courses-based
    const activeCourses = (institute?.courses || []).filter(c => c.isActive !== false);
    const courseOptions = activeCourses.map(c => c.name);

    // Batch options derived from the selected course (with year ± 5 fallback)
    const selectedCourseObj = activeCourses.find(c => c.name === course);
    const batchOptions = (() => {
        const courseBatches = selectedCourseObj
            ? (Array.isArray(selectedCourseObj.batches) ? selectedCourseObj.batches : [])
            : [];
        if (courseBatches.length > 0) return courseBatches;
        // Fallback: auto-populate with current year ± 8
        if (selectedCourseObj) {
            const currentYear = new Date().getFullYear();
            return Array.from({ length: 17 }, (_, i) => String(currentYear - 8 + i));
        }
        return [];
    })();

    const dietOptions = (institute?.diets && institute.diets.length > 0)
        ? institute.diets
        : ['Veg', 'Non-Veg'];

    const hostelOptions = institute?.hostels || [];

    // [FEATURE] Form progress persistence — save to sessionStorage
    const FORM_KEY = 'resido_profile_form';

    useEffect(() => {
        try {
            const saved = sessionStorage.getItem(FORM_KEY);
            if (saved) {
                const data = JSON.parse(saved);
                if (data.name) setName(data.name);
                if (data.course) setCourse(data.course);
                if (data.batch) setBatch(data.batch);
                if (data.diet) setDiet(data.diet);
                if (data.hostel) setHostel(data.hostel);
                if (data.contact) setContact(data.contact);
            }
        } catch (e) { /* ignore parse errors */ }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                sessionStorage.setItem(FORM_KEY, JSON.stringify({ name, course, batch, diet, hostel, contact }));
            } catch (e) { /* ignore quota errors */ }
        }, 500); // debounce
        return () => clearTimeout(timer);
    }, [name, course, batch, diet, hostel, contact]);

    // Countdown timer
    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setInterval(() => {
            setCountdown(prev => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [countdown]);

    // Auto-verify when 6 digits entered
    useEffect(() => {
        if (otpCode.length === 6 && isOtpSent && !isOtpVerified && !isVerifyingOtp) {
            handleVerifyOtp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpCode]);

    // [SECURITY] Clean up reCAPTCHA on unmount + clear DOM
    useEffect(() => {
        return () => {
            if (recaptchaVerifierRef.current) {
                try { recaptchaVerifierRef.current.clear(); } catch (e) { /* ignore */ }
                recaptchaVerifierRef.current = null;
            }
            // [SECURITY] Also clear the DOM element to prevent memory leaks
            if (recaptchaContainerRef.current) {
                recaptchaContainerRef.current.innerHTML = '';
            }
            // Clean up form data on unmount (profile saved or user left)
        };
    }, []);

    const setupRecaptcha = useCallback(() => {
        // 1. Clear the old verifier instance
        if (recaptchaVerifierRef.current) {
            try { recaptchaVerifierRef.current.clear(); } catch (e) { /* ignore */ }
            recaptchaVerifierRef.current = null;
        }

        // 2. Completely destroy and recreate the DOM container
        // This fixes "reCAPTCHA has already been rendered in this element"
        const oldContainer = document.getElementById('recaptcha-container');
        if (oldContainer) {
            const parent = oldContainer.parentNode;
            oldContainer.remove();
            const newContainer = document.createElement('div');
            newContainer.id = 'recaptcha-container';
            parent.appendChild(newContainer);
        }

        // 3. Create a fresh verifier
        recaptchaVerifierRef.current = new RecaptchaVerifier(authInstance, 'recaptcha-container', {
            size: 'invisible',
            callback: () => { },
            'expired-callback': () => {
                setOtpError('reCAPTCHA expired. Please try again.');
            }
        });
    }, [authInstance]);

    // [SECURITY] Rate-limit OTP resend — persist countdown in sessionStorage
    const OTP_COOLDOWN_KEY = 'resido_otp_cooldown';

    const handleSendOtp = async () => {
        // Validate phone format
        const phoneCheck = validatePhone(contact);
        if (!phoneCheck.valid) {
            setPhoneError(phoneCheck.error);
            return;
        }
        setPhoneError('');

        // [SECURITY] Check persisted cooldown (survives page refresh)
        try {
            const savedCooldown = sessionStorage.getItem(OTP_COOLDOWN_KEY);
            if (savedCooldown) {
                const remaining = Math.ceil((parseInt(savedCooldown) - Date.now()) / 1000);
                if (remaining > 0) {
                    setCountdown(remaining);
                    setOtpError(`Please wait ${remaining}s before requesting another code.`);
                    return;
                }
            }
        } catch (e) { /* ignore */ }

        setIsSendingOtp(true);
        setOtpError('');
        setOtpCode('');
        try {
            setupRecaptcha();
            const phoneNumber = `+91${contact}`;
            const provider = new PhoneAuthProvider(authInstance);
            const verId = await provider.verifyPhoneNumber(phoneNumber, recaptchaVerifierRef.current);
            setVerificationId(verId);
            setIsOtpSent(true);
            setCountdown(60);
            // [SECURITY] Persist cooldown timestamp
            sessionStorage.setItem(OTP_COOLDOWN_KEY, String(Date.now() + 60000));
        } catch (err) {
            SecureLog.error("Send OTP error:", err);
            if (err.code === 'auth/too-many-requests') {
                setOtpError('Too many attempts. Please try again later.');
            } else if (err.code === 'auth/invalid-phone-number') {
                setOtpError('Invalid phone number format.');
            } else if (err.code === 'auth/invalid-app-credential') {
                setOtpError('Phone verification is temporarily unavailable. Please try again in a moment.');
            } else if (err.message?.includes('already been rendered')) {
                // Retry once after destroying reCAPTCHA
                setOtpError('Verification setup failed. Please tap "Send OTP" again.');
            } else {
                setOtpError('Failed to send OTP. Please try again.');
            }
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!verificationId || otpCode.length !== 6) return;
        setIsVerifyingOtp(true);
        setOtpError('');
        try {
            const credential = PhoneAuthProvider.credential(verificationId, otpCode);
            try {
                // Try to link phone to the Google-authenticated account
                await linkWithCredential(user, credential);
            } catch (linkErr) {
                // If phone is already linked (re-verification scenario) or
                // provider already exists, the OTP was still valid
                if (linkErr.code === 'auth/credential-already-in-use' ||
                    linkErr.code === 'auth/provider-already-linked') {
                    SecureLog.warn('[ProfileSetup] Phone already linked, OTP still valid:', linkErr.code);
                    // OTP was verified successfully by Firebase even though linking failed
                } else {
                    // Re-throw unexpected errors
                    throw linkErr;
                }
            }
            setIsOtpVerified(true);
            // [ANIMATION] Trigger confetti on success
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 2000);
        } catch (err) {
            SecureLog.error("Verify OTP error:", err);
            if (err.code === 'auth/invalid-verification-code') {
                setOtpError('Invalid OTP. Please check and try again.');
            } else if (err.code === 'auth/code-expired') {
                setOtpError('OTP has expired. Please request a new one.');
            } else if (err.code === 'auth/invalid-verification-id') {
                setOtpError('Verification session expired. Please request a new OTP.');
            } else {
                setOtpError(`Verification failed: ${err.message || 'Please try again.'}`);
            }
            setOtpCode('');
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const handleResendOtp = () => {
        if (countdown > 0) return;
        setIsOtpSent(false);
        setOtpCode('');
        setOtpError('');
        setTimeout(() => handleSendOtp(), 300);
    };

    const isFormValid = name.trim() && !nameError && course && batch && diet && hostel && contact.length === 10 && !phoneError && isOtpVerified;

    const handleSaveProfile = async () => {
        // Final validation
        const nameCheck = validateName(name);
        if (!nameCheck.valid) { setNameError(nameCheck.error); return; }

        setIsSaving(true);
        setError('');
        try {
            let gender = 'Other';
            const hostelLower = hostel.toLowerCase();
            if (hostelLower.includes('boy')) gender = 'Male';
            else if (hostelLower.includes('girl') || hostelLower.includes('ladies')) gender = 'Female';

            const profileData = {
                uid: user.uid,
                instituteId: institute.id,
                instituteName: institute.name,
                email: user.email,
                name: name.trim(),
                course,
                batch,
                diet,
                contact,
                isPhoneVerified: true,
                userType: 'iOS/Web',
                photoUrl: user.photoURL || null,
                hostel,
                gender,
                createdAt: new Date(),
            };
            await setDoc(doc(db, "users", user.uid), profileData);
            // Clear saved form data
            try { sessionStorage.removeItem(FORM_KEY); } catch (e) { /* ignore */ }
            setShowConfirmDialog(false);
            navigate('/dashboard');
        } catch (err) {
            SecureLog.error("Save profile error:", err);
            setError('Failed to save profile. Please try again.');
            setShowConfirmDialog(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = () => {
        try { sessionStorage.removeItem(FORM_KEY); } catch (e) { /* ignore */ }
        signOut(authInstance).then(() => navigate('/')).catch(SecureLog.error);
    };

    if (!institute) return null;

    const currentStep = isOtpVerified ? 3 : 2;

    return (
        <div
            className="min-h-screen relative overflow-hidden"
            style={{ backgroundColor: THEME.colors.midnightBlue, fontFamily: THEME.fonts.body }}
        >
            {/* --- LIVING BACKGROUND --- */}
            <div className="absolute top-0 -left-4 w-72 h-72 bg-amber-500 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>

            {/* Invisible reCAPTCHA container */}
            <div id="recaptcha-container" ref={recaptchaContainerRef}></div>

            {/* --- CONTENT --- */}
            <div className="relative z-10 max-w-lg mx-auto px-4 py-8 min-h-screen flex flex-col" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}>
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                    <button
                        onClick={() => navigate('/institute-selection')}
                        className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-800"
                        title="Back"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white">
                        Setup Profile
                    </h1>
                    <button
                        onClick={handleLogout}
                        className="text-slate-400 hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-red-500/10"
                        title="Sign Out"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </button>
                </div>

                {/* Step Indicator */}
                <StepIndicator currentStep={currentStep} />

                {/* User Avatar + Email Card */}
                <div
                    className="mb-6 p-4 rounded-2xl border border-slate-700/50 flex items-center gap-4 animate-fade-in-up"
                    style={{ backgroundColor: THEME.colors.midnightSurface }}
                >
                    <div className="relative">
                        {user?.photoURL ? (
                            <img src={user.photoURL} alt="Profile" className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-500/30" />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                                {(user?.displayName || user?.email || '?')[0].toUpperCase()}
                            </div>
                        )}
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-teal-500 rounded-full border-2 border-slate-800 flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        </div>
                    </div>
                    <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{user?.displayName || 'New User'}</p>
                        <p className="text-slate-500 text-xs truncate">{user?.email}</p>
                    </div>
                </div>

                {/* Institute Badge */}
                <div className="mb-6 p-3.5 rounded-xl border border-slate-700/30 flex items-center gap-3" style={{ backgroundColor: `${THEME.colors.midnightSurface}90` }}>
                    <div className="p-2 bg-amber-500/10 rounded-lg text-amber-500 flex-shrink-0">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{institute.name}</p>
                        {institute.shortName && <p className="text-slate-500 text-xs">{institute.shortName}</p>}
                    </div>
                </div>

                {error && (
                    <div className="bg-red-900/20 border border-red-500/40 text-red-200 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-2">
                        <svg className="w-5 h-5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>{error}</span>
                    </div>
                )}

                {/* --- FORM with slide animation --- */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        variants={contentVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        className="flex-1 space-y-5"
                    >

                        {/* ============= SECTION 1: Personal Info ============= */}
                        <div
                            className="rounded-2xl border border-slate-700/40 p-5 space-y-4"
                            style={{ backgroundColor: `${THEME.colors.midnightSurface}80` }}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                <h2 className="text-xs text-slate-400 uppercase tracking-wider font-bold">Personal Information</h2>
                            </div>

                            {/* [SECURITY] Name with validation */}
                            <div>
                                <label className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 block">Full Name</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-500">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                    </div>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setName(val);
                                            if (val.trim()) {
                                                const check = validateName(val);
                                                setNameError(check.valid ? '' : check.error);
                                            } else {
                                                setNameError('');
                                            }
                                        }}
                                        className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm transition-all duration-200 focus:outline-none"
                                        style={{
                                            backgroundColor: THEME.colors.inputBg,
                                            border: `1px solid ${nameError ? '#EF4444' : name ? THEME.colors.goldAccent + '40' : THEME.colors.borderColor}`,
                                            color: 'white'
                                        }}
                                        placeholder="Your full name"
                                        maxLength={50}
                                    />
                                </div>
                                {nameError && (
                                    <motion.p
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="text-red-400 text-xs mt-1.5 ml-1"
                                    >{nameError}</motion.p>
                                )}
                            </div>

                            {/* Course Selection */}
                            {courseOptions.length > 0 && (
                                <SelectField
                                    label="Course"
                                    value={course}
                                    onChange={(val) => {
                                        setCourse(val);
                                        setBatch(''); // Reset batch when course changes
                                    }}
                                    options={courseOptions}
                                    placeholder="Select your course"
                                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
                                    tooltip="Select the course you are enrolled in. Batch options will update accordingly."
                                />
                            )}

                            {/* Batch & Diet Row */}
                            <div className="grid grid-cols-2 gap-3">
                                <SelectField
                                    label="Batch / Year"
                                    value={batch}
                                    onChange={setBatch}
                                    options={batchOptions}
                                    placeholder={course ? 'Select' : 'Select course first'}
                                    disabled={!course && courseOptions.length > 0}
                                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                                />
                                <SelectField
                                    label="Diet"
                                    value={diet}
                                    onChange={setDiet}
                                    options={dietOptions}
                                    placeholder="Select"
                                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" /></svg>}
                                />
                            </div>

                            {/* [FEATURE] Hostel with tooltip */}
                            {hostelOptions.length > 0 && (
                                <SelectField
                                    label="Hostel"
                                    value={hostel}
                                    onChange={setHostel}
                                    options={hostelOptions}
                                    placeholder="Select your hostel"
                                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>}
                                    tooltip="Select the hostel assigned to you by your institute. This determines your mess billing."
                                />
                            )}
                        </div>

                        {/* ============= SECTION 2: Phone Verification ============= */}
                        <div
                            className="rounded-2xl border border-slate-700/40 p-5"
                            style={{ backgroundColor: `${THEME.colors.midnightSurface}80` }}
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                <h2 className="text-xs text-slate-400 uppercase tracking-wider font-bold">Phone Verification</h2>
                                {isOtpVerified && (
                                    <span className="ml-auto text-xs text-teal-400 font-medium flex items-center gap-1">
                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                        Verified
                                    </span>
                                )}
                            </div>

                            {/* Phone Input with validation feedback */}
                            <div className="flex items-stretch gap-2 mb-1">
                                <div
                                    className="flex items-center justify-center px-4 rounded-xl text-sm font-semibold border flex-shrink-0"
                                    style={{
                                        backgroundColor: THEME.colors.inputBg,
                                        borderColor: isOtpVerified ? THEME.colors.tealAccent : THEME.colors.borderColor,
                                        color: isOtpVerified ? THEME.colors.tealAccent : THEME.colors.textSecondary,
                                    }}
                                >
                                    +91
                                </div>
                                <input
                                    type="tel"
                                    value={contact}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setContact(val);
                                        if (val.length > 0) {
                                            const check = validatePhone(val);
                                            setPhoneError(val.length === 10 ? (check.valid ? '' : check.error) : '');
                                        } else {
                                            setPhoneError('');
                                        }
                                        if (isOtpSent) {
                                            setIsOtpSent(false);
                                            setOtpCode('');
                                            setOtpError('');
                                            setIsOtpVerified(false);
                                        }
                                    }}
                                    disabled={isOtpVerified}
                                    className="flex-1 px-4 py-3.5 rounded-xl text-sm transition-all duration-200 focus:outline-none disabled:opacity-50"
                                    style={{
                                        backgroundColor: THEME.colors.inputBg,
                                        border: `1px solid ${phoneError ? '#EF4444' : contact ? THEME.colors.goldAccent + '40' : THEME.colors.borderColor}`,
                                        color: 'white',
                                    }}
                                    placeholder="10-digit phone number"
                                    inputMode="numeric"
                                />
                            </div>
                            {phoneError && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-red-400 text-xs mt-1 ml-1 mb-2"
                                >{phoneError}</motion.p>
                            )}

                            {/* Send OTP Button */}
                            {!isOtpSent && !isOtpVerified && (
                                <button
                                    onClick={handleSendOtp}
                                    disabled={contact.length !== 10 || isSendingOtp || !!phoneError}
                                    className="w-full py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-3"
                                    style={{
                                        backgroundColor: contact.length === 10 && !phoneError ? `${THEME.colors.goldAccent}15` : 'transparent',
                                        border: `1px solid ${contact.length === 10 && !phoneError ? THEME.colors.goldAccent + '40' : THEME.colors.borderColor}`,
                                        color: contact.length === 10 && !phoneError ? THEME.colors.goldAccent : THEME.colors.textSecondary,
                                    }}
                                >
                                    {isSendingOtp ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                                            Send Verification Code
                                        </>
                                    )}
                                </button>
                            )}

                            {/* OTP Input Section */}
                            {isOtpSent && !isOtpVerified && (
                                <div
                                    className="p-5 rounded-xl border mt-3"
                                    style={{ backgroundColor: THEME.colors.midnightSurface, borderColor: THEME.colors.borderColor }}
                                >
                                    <p className="text-slate-400 text-xs text-center mb-4">
                                        Enter the 6-digit code sent to <span className="text-white font-medium">+91 {contact}</span>
                                    </p>

                                    <OtpInput
                                        value={otpCode}
                                        onChange={setOtpCode}
                                        disabled={isVerifyingOtp}
                                    />

                                    {isVerifyingOtp && (
                                        <div className="flex items-center justify-center gap-2 mt-4 text-amber-400 text-sm">
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Verifying...
                                        </div>
                                    )}

                                    <div className="mt-4 text-center">
                                        {countdown > 0 ? (
                                            <p className="text-slate-500 text-xs">Resend in <span className="text-slate-300 font-mono">{countdown}s</span></p>
                                        ) : (
                                            <button onClick={handleResendOtp} className="text-amber-500 hover:text-amber-400 text-xs font-medium transition-colors">
                                                Resend Code
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* [ANIMATION] Verified Success Badge with confetti burst */}
                            {isOtpVerified && (
                                <div className="relative">
                                    <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center gap-3 mt-2 animate-fade-in-up">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 300, damping: 12 }}
                                            className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0"
                                        >
                                            <svg className="w-6 h-6 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                                <motion.path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M5 13l4 4L19 7"
                                                    initial={{ pathLength: 0 }}
                                                    animate={{ pathLength: 1 }}
                                                    transition={{ duration: 0.5, delay: 0.2 }}
                                                />
                                            </svg>
                                        </motion.div>
                                        <div>
                                            <p className="text-teal-300 text-sm font-semibold">Phone Verified!</p>
                                            <p className="text-teal-400/60 text-xs">+91 {contact} linked to your account</p>
                                        </div>
                                    </div>

                                    {/* [ANIMATION] Confetti burst */}
                                    {showConfetti && (
                                        <div className="absolute inset-0 pointer-events-none overflow-visible flex items-center justify-center">
                                            {Array.from({ length: 12 }).map((_, i) => (
                                                <motion.div
                                                    key={i}
                                                    custom={i}
                                                    variants={confettiVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    className="absolute w-2 h-2 rounded-full"
                                                    style={{ backgroundColor: confettiColors[i % confettiColors.length] }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* OTP Error */}
                            {otpError && (
                                <motion.div
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-2 p-3 rounded-xl bg-red-900/20 border border-red-500/30 text-red-300 text-sm flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    {otpError}
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Save Profile Button */}
                <div className="mt-8 pb-4">
                    <motion.button
                        onClick={() => setShowConfirmDialog(true)}
                        disabled={!isFormValid || isSaving}
                        className="w-full font-bold text-lg py-4 px-8 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                            background: isFormValid
                                ? `linear-gradient(135deg, ${THEME.colors.goldAccent}, #D97706)`
                                : THEME.colors.borderColor,
                            boxShadow: isFormValid ? '0 4px 14px 0 rgba(245,158,11,0.25)' : 'none',
                            color: isFormValid ? THEME.colors.midnightBlue : THEME.colors.textSecondary,
                        }}
                        whileTap={isFormValid ? { scale: 0.97 } : {}}
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        Save Profile
                    </motion.button>
                </div>
            </div>

            {/* --- CONFIRMATION DIALOG --- */}
            <AnimatePresence>
                {showConfirmDialog && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
                            className="rounded-2xl shadow-2xl p-8 w-full max-w-md border border-slate-700"
                            style={{ backgroundColor: THEME.colors.midnightSurface }}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-teal-500/10 rounded-full text-teal-400">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white">Confirm Profile</h3>
                            </div>

                            <p className="text-slate-400 text-sm mb-6">
                                Please review your details before saving:
                            </p>

                            <div className="bg-slate-900/50 rounded-xl border border-slate-800 divide-y divide-slate-800">
                                {[
                                    { label: 'Name', value: name, color: 'text-white' },
                                    { label: 'Institute', value: institute.shortName || institute.name, color: 'text-white' },
                                    { label: 'Course', value: course, color: 'text-white' },
                                    { label: 'Batch', value: batch, color: 'text-white' },
                                    { label: 'Diet', value: diet, color: 'text-amber-400' },
                                    { label: 'Hostel', value: hostel, color: 'text-white' },
                                ].filter(row => row.value).map(row => (
                                    <div key={row.label} className="flex justify-between px-4 py-3">
                                        <span className="text-slate-500 text-sm">{row.label}</span>
                                        <span className={`${row.color} text-sm font-medium`}>{row.value}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between px-4 py-3">
                                    <span className="text-slate-500 text-sm">Phone</span>
                                    <span className="text-teal-400 text-sm font-medium flex items-center gap-1">
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                        +91 {contact}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowConfirmDialog(false)}
                                    disabled={isSaving}
                                    className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={isSaving}
                                    className="px-6 py-2.5 rounded-lg font-bold text-slate-900 text-sm shadow-md transition-transform active:scale-95 flex items-center gap-2 disabled:opacity-70"
                                    style={{ background: THEME.colors.goldAccent }}
                                >
                                    {isSaving ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Saving...
                                        </>
                                    ) : (
                                        'Confirm & Save'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default ProfileSetup;
